import * as Commons from "oc-commons/Commons";
import * as Scenes from "oc-threejs/Scenes";
import { UniversalLoader } from "oc-threejs/loader/UniversalLoader";
import type { Jaco } from "../arrangement/Jaco";
import { Urls } from "oc-commons/Urls";
import { ReplacingMaterialFactory } from "oc-threejs/loader/ReplacingMaterialFactory";
import TWEEN from "@tweenjs/tween.js";
import { v3 } from "oc-threejs/short3";
import { Group, MathUtils, Object3D, Quaternion, Vector3 } from "three";

export type Constructor<T = {}> = new (...args: any[]) => T;

/**
 * @template {{new (...args: any[]) => {}} Super
 * @param {Super} superClass
 */
export function Jaco3dMixin<TBase extends Constructor<Object3D>>(superClass: TBase = Group as any) {
  return class Jaco3dClass extends superClass {
    // Mittels Jaco3D.Mixin(BasisKlasse) kann man Jaco3d als Mixin benutzen, d.h. von einer anderen Klasse als Group erben lassen
    // das ist praktisch, wenn man z.B. statt dessen von THREE.Line ableiten möche
    static Mixin = Jaco3dMixin;

    jaco: Jaco;
    forbidAutoRemove = false;
    _updateId = -1;
    _jacoOld: Record<string, any> = {};
    _jacoChanged: Record<string, boolean> = {};
    _animations: Record<string, any> | null = null;
    draggable?: boolean; // From usage

    constructor(...args: any[]);
    constructor(jaco: Jaco, ...args: any[]) {
      super(...args);
      // Mixin constructor handling can be tricky, assuming first arg is jaco if provided?
      // In JS code: constructor(jaco) { super(); this.jaco = jaco; ... }
      // But superClass might expect args.
      // However, usually Jaco3d(jaco) is called.
      this.jaco = jaco;
      this.name = jaco?.getJaco3dName();
    }

    /**
     * Sinn dieser Methode ist es, in den Typen die Materialien, Geometrien etc. nur bei Bedarf zu ändern.
     * Gibt zurück, ob ein Wert aus JacoObj sich geändert hat im Vergleich zum letztmaligen update3d-Aufruf
     * @param {string} path
     * @param {*} [accessor=jaco => jaco ? jaco[path] : undefined]
     * @returns {boolean}
     */
    changed(
      path: string,
      accessor: (jaco: Jaco) => any = (jaco) => (jaco ? (jaco as any)[path] : undefined),
    ): boolean {
      const buffered = this._jacoChanged[path];
      if (buffered !== undefined) {
        return buffered;
      }
      const valueNew = accessor(this.jaco);
      const valueOld = this._jacoOld[path];
      const changed = (this._jacoChanged[path] = !Commons.isEqualEpsilon(valueOld, valueNew));
      if (changed) {
        this._jacoOld[path] = Commons.cloneDeep(valueNew);
      }
      return changed;
    }

    /**
     * Diese Methode muss im Rahmen des jaco-update3d aufgerufen werden. Objekte, die nicht
     * touched sind, und nicht forbidAutoRemove=false haben, werden abgeräumt
     */
    setTouchedByJaco() {
      this._updateId = APP._currentUpdateId;
      this._jacoChanged = {};
    }

    /**
     * Kopiert Standardmäßig Drehung und Position des Grundobjekts, und Opacity/visibility
     * Überschreiben falls das nicht gewünscht ist.
     */
    update3d() {
      this.copyCoordinateSystem(this.jaco);
      this.copyOpacity(this.jaco);
    }

    /** */
    copyOpacity(jaco: Jaco) {
      this.visible = jaco.visible;
      this.maybeAnimate(
        jaco,
        "opacity",
        (a: any) => a,
        (object: any) => object.opacity ?? 1,
        (start: any, end: any) => start == end,
        (end: any) => ((this as any).opacity = end),
        (start: any, end: any, t: number) =>
          ((this as any).opacity = MathUtils.lerp(start, end, t)),
      );
    }

    /** */
    copyCoordinateSystem(jaco: Jaco) {
      this.maybeAnimate(
        jaco,
        "position",
        (a: Vector3) => a.clone(),
        (object: any) => object.position,
        (start: Vector3, end: Vector3) => start.equals(end),
        (end: Vector3) => this.position.copy(end),
        (start: Vector3, end: Vector3, t: number) => this.position.lerpVectors(start, end, t),
      );
      this.maybeAnimate(
        jaco,
        "scale",
        (a: Vector3) => a.clone(),
        (object: any) => object.scale,
        (start: Vector3, end: Vector3) => start.equals(end),
        (end: Vector3) => this.scale.copy(end),
        (start: Vector3, end: Vector3, t: number) => this.scale.lerpVectors(start, end, t),
      );
      this.maybeAnimate(
        jaco,
        "quaternion",
        (a: Quaternion) => a.clone(),
        (object: any) => object.quaternion,
        (start: Quaternion, end: Quaternion) => start.equals(end),
        (end: Quaternion) => this.quaternion.copy(end),
        (start: Quaternion, end: Quaternion, t: number) =>
          this.quaternion.slerpQuaternions(start, end, t),
      );
    }

    maybeAnimate<T>(
      target: Jaco,
      property: string,
      clone: (val: T) => T,
      get: (obj: any) => T,
      equals: (a: T, b: T) => boolean,
      endCallback: (val: T) => void,
      updateCallback?: (start: T, end: T, t: number) => void,
    ) {
      const transitionProperty = this.jaco.transition?.property;
      const array: string[] =
        transitionProperty == null
          ? []
          : Array.isArray(transitionProperty)
            ? transitionProperty
            : [transitionProperty];
      if (array.some((p) => p === "all" || p === property)) {
        if (equals(get(this), get(target))) {
          this._removeAnimation(property);
          endCallback(get(target));
        } else {
          let animation = this._animations?.[property];
          if (animation) {
            if (equals(animation.endValue, get(target))) {
              // nichts zu tun
              return;
            }
          }
          const now = performance.now();
          const duration = this.jaco.transition?.duration ?? 1000;
          const delay = this.jaco.transition?.delay ?? 0;
          STAGE.setDirtyForMs(duration + delay);
          const startTime = now + delay;
          const endTime = startTime + duration;
          const startValue = clone(get(this));
          const endValue = clone(get(target));
          const timingFunction =
            this.jaco.transition?.timingFunction ?? TWEEN.Easing.Sinusoidal.InOut;
          this._animations = this._animations ?? {};
          this._animations![property] = {
            duration,
            startTime,
            endTime,
            startValue,
            endValue,
            updateCallback,
            endCallback,
            timingFunction,
          };
        }
      } else {
        endCallback(get(target));
      }
    }

    _removeAnimation(property: string) {
      if (this._animations?.[property]) {
        delete this._animations[property];
        if (Object.keys(this._animations).length == 0) {
          this._animations = null;
        }
      }
    }

    // @ts-ignore - access optional super method
    onBeforeRenderStage() {
      if (super.onBeforeRenderStage as any) (super.onBeforeRenderStage as any)();
      if (this._animations == null) {
        return;
      }
      const now = performance.now();
      for (const [property, animation] of Object.entries(this._animations)) {
        if (now < animation.startTime) {
          // wait...
        } else if (now < animation.endTime) {
          const a = (now - animation.startTime) / animation.duration;
          const b = animation.timingFunction(a);
          const t = MathUtils.clamp(b, 0, 1);
          if (animation.updateCallback) {
            animation.updateCallback(animation.startValue, animation.endValue, t);
          }
        } else {
          animation.endCallback(animation.endValue);
          this._removeAnimation(property);
        }
      }
    }

    /**
     * @param modelMaterialRelation
     * @param {(THREE.Group)=>void} onLoadCallback
     * @returns {THREE.Group}
     */
    loadObject3DByModelMaterialRelation(
      modelMaterialRelation: any,
      onLoadCallback?: (group: Group) => void,
    ): Group {
      APP.checkMMRNotUndefined(modelMaterialRelation);

      let group = new Group();

      // dummyObjekt - Workaround: Laden ist asynchron. Wenn das Objekt leer ist - also gar keine Geometrie
      // enthält - dann klappt drag&drop nicht, das Objekt kann nicht in die Szene gedraggt werden (bounding box?)
      let loadingDummy: Object3D | null = null;
      let options = modelMaterialRelation; // Assuming options matches mmr or similar

      if (this.draggable || STAGE.settings.alwaysShowLoadingDummy) {
        // TODO evtl. loadingDummy cachen?
        loadingDummy = this.createLoadingDummy(options);
        if (loadingDummy) {
          group.add(loadingDummy);
        }
        STAGE.dirty();
      }

      Commons.delayUntil(
        () => {
          const cacheKey = Commons.cacheKey(
            modelMaterialRelation.Model.Path,
            modelMaterialRelation.MaterialMap,
          );
          let promise = UniversalLoader.loadModelPromise(
            this.getModelUrls(modelMaterialRelation),
            this.getMaterialFactory(modelMaterialRelation),
            cacheKey,
          );
          promise = UniversalLoader.getPromiseAllImagesLoaded(promise);
          promise.then(
            (object: Object3D | null) => {
              if (object == null) {
                return;
              }
              if (loadingDummy) Scenes.remove(loadingDummy);
              this.transformObject3dAfterLoad(object, modelMaterialRelation.Model);
              group.add(object);
              if (onLoadCallback) {
                onLoadCallback(group);
              }
              STAGE.dirty();
            },
            (error: any) => {
              //404 werden bereits vom UniversalLoader auf console ausgegeben
            },
          );
        },
        () => UniversalLoader.getMaterialFactory() != null,
      );

      return group;
    }

    /**
     * @returns {string[]}
     */
    getModelUrls(modelMaterialRelation: any): string[] {
      const path = modelMaterialRelation.Model.Path;
      if (path == null) {
        return [];
      }
      let urls: string[] = path.split(",");
      const [hasOBJ, hasMTL] = [".obj", ".mtl"].map((suffix) =>
        urls.some((s) => s.endsWith(suffix)),
      );
      if (hasMTL && !hasOBJ) {
        const obj = urls[0].replace(/.mtl$/i, ".obj");
        urls.push(obj);
      }
      const absoluteUrls = urls.map((url) =>
        Urls.isAbsolute(url) ? url : APP.settings.wkfgContentBaseUrl + url,
      );
      return absoluteUrls;
    }

    /**
     * @returns {ReplacingMaterialFactory|ReplacingMaterialFactory}
     */
    getMaterialFactory(modelMaterialRelation: any) {
      return new ReplacingMaterialFactory(
        modelMaterialRelation.MaterialMap,
        UniversalLoader.getMaterialFactory(),
      );
    }

    /**
     * @param {THREE.Object3D} object
     */
    transformObject3dAfterLoad(object: Object3D, model: any) {
      APP.modelsHelper.applyToObject3d(model, object);
      return object;
    }

    /**
     * @returns {THREE.Object3D}
     */
    createLoadingDummy(options: any): Object3D {
      const boundingBox = APP.modelsHelper.getBoundingBox(options);
      const box = Scenes.box(boundingBox.getSize(v3()), {
        color: 0xa0a0a0,
        transparent: true,
        opacity: 0.5,
      });
      boundingBox.getCenter(box.position);
      return box;
    }
  };
}

export const Jaco3d = Jaco3dMixin();
export type Jaco3d = InstanceType<typeof Jaco3d>;
